import React, { useState } from 'react';
import { RegisterDeviceContracts } from '../Async/RegisterDevicesAsync';
import { SearchDeviceContracts } from '../Async/SearchDevicesAsync';
import { DeviceStatus } from '../Async/ServicesAustraliaModels';
import { AlertBanner, AlertBannerTypes } from '../Components/AlertBanner';
import { Button } from '../Components/Button';
import { FullScreenModal } from '../Components/FullScreenModal';
import { DangerIcon } from '../Components/Icons';
import { displayMessage, getConfig } from '../Config/ApplicationConfig';
import { Nullable } from '../Shared/Types';
import { dangerVariant } from '../Styles/Colours';
import { RegisterDeviceForm, RegisterDeviceFormState } from './RegisterDeviceForm';
import styles from './RegisterDeviceWizard.module.scss';

function mapSearchResultToInitialFormState(
    targetDevice: SearchDeviceContracts.Response.DevicesSearchResult
): RegisterDeviceFormState {
    return {
        customerName: targetDevice.customer.name ?? '',
        customerAuthSystem: targetDevice.customer.authoringSystem,
        customerAuthSystemKey: targetDevice.customer.authoringSystemForeignKey,
        deviceName: targetDevice.prodaDeviceName,
        deviceActivationCode: '', // TODO: Source this
        deviceOrganisationIdentifier: targetDevice.prodaOrganisationIdentifier,
        deviceAuthoringSystem: targetDevice.authoringSystem,
        deviceAuthoringSystemKey: targetDevice.authoringSystemForeignKey,
        deviceQueueName: targetDevice.instanceQueueName,
        healthSystemApplicationName: targetDevice.applicationName,
        healthSystemApplicationClientIdentifier: targetDevice.applicationClientIdentifier,
        healthSystemApplicationXIBMClientIdentifier: getConfig().proda.xibmClientIdentifier // TODO: Source this
    };
}

enum DeviceWizardStep {
    Form,
    Result
}

function DeviceRegistration(props: {
    initialValues: RegisterDeviceFormState | undefined;
    lastResult: Nullable<RegisterDeviceContracts.Response.DeviceCreateResult>;
    onClose: () => void;
    wizardStep: DeviceWizardStep;
    onSuccess: (result: RegisterDeviceContracts.Response.DeviceCreateResult, form: RegisterDeviceFormState) => void;
    showResult: boolean;
}) {
    if (props.wizardStep === DeviceWizardStep.Form) {
        return <RegisterDeviceForm onSuccess={props.onSuccess} initialValues={props.initialValues} />;
    } else if (props.showResult) {
        if (props.lastResult?.errors && props.lastResult.errors.length > 0) {
            return <RegisterDeviceResultViewer onContinue={props.onClose} lastResult={props.lastResult} />;
        } else {
            displayMessage('Device registration successful');
            props.onClose();
            return null;
        }
    } else return <div>Error!</div>;
}

export function RegisterDeviceWizard(props: {
    onClose: () => void;
    targetDevice?: SearchDeviceContracts.Response.DevicesSearchResult;
}) {
    const [wizardStep, setWizardStep] = useState(DeviceWizardStep.Form);
    const [lastResult, setLastResult] = useState<Nullable<RegisterDeviceContracts.Response.DeviceCreateResult>>(null);

    const [formValues, setFormValues] = useState(
        props.targetDevice ? mapSearchResultToInitialFormState(props.targetDevice) : undefined
    );

    function deviceRegisteredWithResult(
        result: RegisterDeviceContracts.Response.DeviceCreateResult,
        form: RegisterDeviceFormState
    ) {
        setLastResult(result);
        setFormValues(form);
        setWizardStep(DeviceWizardStep.Result);
    }

    const modalTitle =
        wizardStep === DeviceWizardStep.Form
            ? 'Register device'
            : wizardStep === DeviceWizardStep.Result && lastResult?.status === DeviceStatus.Error
            ? 'Uh oh! There was a problem registering the device'
            : '';

    const returnToFormStep =
        wizardStep === DeviceWizardStep.Result ? () => setWizardStep(DeviceWizardStep.Form) : undefined;

    const showResult = wizardStep === DeviceWizardStep.Result && !!lastResult;

    const errorIcon =
        showResult && lastResult?.errors.length > 0 ? (
            <div className={styles.errorIcon}>
                <DangerIcon noPadding size={'56'} color={dangerVariant} />
            </div>
        ) : null;

    return (
        <FullScreenModal
            title={modalTitle}
            info=''
            onClose={props.onClose}
            onBack={returnToFormStep}
            alertContent={errorIcon}
            slim={showResult}
        >
            <DeviceRegistration
                wizardStep={wizardStep}
                onSuccess={deviceRegisteredWithResult}
                initialValues={formValues}
                lastResult={lastResult}
                onClose={props.onClose}
                showResult={showResult}
            />
        </FullScreenModal>
    );
}

function RegisterDeviceResultViewer(props: {
    onContinue: () => void;
    lastResult: RegisterDeviceContracts.Response.DeviceCreateResult;
}) {
    return (
        <div className={styles.formDetails}>
            {props.lastResult.errors.map((error, index) => (
                <AlertBanner
                    key={`${error.code}-${index}`}
                    roundIcon
                    promptMessage={error.message}
                    promptType={AlertBannerTypes.Error}
                    verticalSpace
                />
            ))}
            <div className={styles.closeButton}>
                <Button
                    width={150}
                    type='submit'
                    testId='Ok'
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.onContinue();
                    }}
                >
                    Close
                </Button>
            </div>
        </div>
    );
}
